/**
 * What the screen says after a save: that it saved, and which of the files sent after it did not
 * take, and why. Keys, never sentences — the page joins them.
 *
 * ── The save succeeded, whatever the files did ──────────────────────────────────────────────
 * The cover and the film are requests AFTER the save (`api/exerciseMutations`), so their failures
 * are reported beside a saved movement, never as a failed save. The phone's own sentences carry
 * „Ćwiczenie zostało zapisane” where they exist: a failed cover, a failed upload of either, both at
 * once.
 *
 * ── A film that did not go for a REASON says so, and where to try again ───────────────────
 * The backend refused it (too large, too long, the wrong type, too many uploads in flight) or the
 * trainer stopped it. Neither is „spróbuj ponownie” on the same screen: the wizard has closed. The
 * sentence is „Film nie został dodany.”, the reason, and that it is added by editing the movement;
 * the save's own sentence goes first, because this one does not say the movement was saved.
 *
 * ── What the CARD shows ─────────────────────────────────────────────────────────────────────
 * Spoken alone, a sighted trainer never sees any of this (owner, 18 September 2026, for the cover).
 * `coverFailureKey` stands in the card's hero until it closes, and so does `filmNote`: the film's
 * own failure, in the same sentence the screen spoke.
 */

import { VIDEO_UPLOAD_FAILED_KEY } from '~/shared/media/video/videoFile';

/**
 * What became of the film: none was chosen, it went up and the server has it as `mediaId`, it did
 * not — with the sentence that says why, as a key — or the trainer stopped it on its way up.
 */
export type VideoUploadOutcome =
  | { readonly status: 'none' }
  | { readonly status: 'sent'; readonly mediaId: number }
  | { readonly status: 'failed'; readonly failureKey: string }
  | { readonly status: 'stopped' };

/** One sentence, as a key, with the key of the reason it names when it names one. */
export interface NoticeSentence {
  readonly key: string;
  /** Interpolated as `{{reason}}`, itself translated. */
  readonly reasonKey?: string;
}

export interface SaveNotice {
  /** The sentences to say, in order. */
  readonly sentences: readonly NoticeSentence[];
  /** The cover's own failure, for the card to show, or `null`. */
  readonly coverFailureKey: string | null;
  /** The film's own failure, for the card to show, or `null`. */
  readonly filmNote: NoticeSentence | null;
}

const FILM_NOT_ADDED = 'web:exerciseLibrary.creator.notice.videoNotAdded';
const UPLOAD_STOPPED = 'web:exerciseLibrary.creator.notice.uploadStopped';

export function saveNotice(
  mode: 'create' | 'edit' | 'duplicate',
  result: { readonly coverUploadFailed: boolean; readonly videoUpload: VideoUploadOutcome },
): SaveNotice {
  const saved =
    mode === 'edit'
      ? 'web:exerciseLibrary.creator.notice.saved'
      : 'web:exerciseLibrary.creator.notice.created';
  const coverFailed = !result.coverUploadFailed
    ? null
    : mode === 'edit'
      ? 'exerciseLibrary.editWizard.coverUploadFailed'
      : 'exerciseLibrary.createWizard.mediaForm.coverUploadFailed';
  const video = result.videoUpload;
  const first = { key: coverFailed ?? saved };

  if (video.status === 'none' || video.status === 'sent') {
    return { sentences: [first], coverFailureKey: coverFailed, filmNote: null };
  }
  if (video.status === 'failed' && video.failureKey === VIDEO_UPLOAD_FAILED_KEY) {
    // The phone's sentences, which already say the movement was saved: one for the film, one for
    // both files at once.
    return {
      sentences: [
        {
          key:
            coverFailed === null
              ? VIDEO_UPLOAD_FAILED_KEY
              : 'exerciseLibrary.createWizard.mediaForm.mediaUploadFailed',
        },
      ],
      coverFailureKey: coverFailed,
      filmNote: { key: VIDEO_UPLOAD_FAILED_KEY },
    };
  }
  const notAdded = {
    key: FILM_NOT_ADDED,
    reasonKey: video.status === 'stopped' ? UPLOAD_STOPPED : video.failureKey,
  };
  return { sentences: [first, notAdded], coverFailureKey: coverFailed, filmNote: notAdded };
}
